import { doc, onSnapshot, setDoc, updateDoc, deleteField } from './services/firebase.js';
import { LOCAL_STORAGE_KEYS, TOG_BACKUP_PREFIX, COLLECTIONS, HALF_DAY_CAPPING_MAX_HOURS, getUserAvatarGradient, generateAvatarSVG } from './constants.js';

let state = {
    viewDate: new Date(),
    lastCalculatedDecimal: 0.0,
    storedData: {},
    dayVisibility: [true, true, true, true, true, true, true], // Mon-Sun
    userId: null,
    db: null,
    auth: null,
    unsubscribe: null,
    isInitialized: false,
    i18n: null,
    trackerYearlyData: {},
    trackerLeaveTypes: [],
    showTrackerData: true,
    showConverters: true,
    showVisibleDays: true,
    halfDayCapping: true,
    expectedDaily: 5.2
};

const DOM = {};

export function initTog(userId, db, auth, i18n) {
    state.userId = userId;
    state.db = db;
    state.auth = auth;
    state.i18n = i18n;

    cacheDOM();

    if (!state.isInitialized) {
        bindEvents();
        state.isInitialized = true;
    }

    // Load local data first if available (for guest mode or offline)
    const local = localStorage.getItem(LOCAL_STORAGE_KEYS.TOG_DATA);
    if(local) {
        try {
            state.storedData = JSON.parse(local);
            if(state.storedData._dayVisibility) state.dayVisibility = state.storedData._dayVisibility;
            if(state.storedData._showConverters !== undefined) state.showConverters = state.storedData._showConverters;
            if(state.storedData._showVisibleDays !== undefined) state.showVisibleDays = state.storedData._showVisibleDays;
            if(state.storedData._halfDayCapping !== undefined) state.halfDayCapping = state.storedData._halfDayCapping;
            if(state.storedData._expectedDaily !== undefined) state.expectedDaily = state.storedData._expectedDaily;
            if(state.storedData._showTrackerData !== undefined) state.showTrackerData = state.storedData._showTrackerData;
        } catch(e) { console.error(e); }
    }

    if (DOM.expectedDaily) DOM.expectedDaily.value = state.expectedDaily;

    applyToggleState();

    // Always render header and calendar immediately with local/cached data
    renderHeader(auth?.currentUser || null);
    renderCalendar();

    if (userId && db) {
        subscribeToData(userId);
    }

    checkAndShowTogOnboarding();
}

function checkAndShowTogOnboarding() {
    const hasSeenTogHelp = localStorage.getItem(LOCAL_STORAGE_KEYS.HAS_SEEN_TOG_HELP);
    if (!hasSeenTogHelp) {
        if (DOM.helpModal) {
            DOM.helpModal.classList.add('visible');
        }
        localStorage.setItem(LOCAL_STORAGE_KEYS.HAS_SEEN_TOG_HELP, 'true');
    }
}

function cacheDOM() {
    DOM.c1_h = document.getElementById('tog-c1_h');
    DOM.c1_m = document.getElementById('tog-c1_m');
    DOM.c2_d = document.getElementById('tog-c2_d');
    DOM.globalMemory = document.getElementById('tog-globalMemory');
    DOM.resDecimal = document.getElementById('tog-res_decimal');
    DOM.resTime = document.getElementById('tog-res_time');

    DOM.monthLabel = document.getElementById('tog-monthLabel');
    DOM.dayToggles = document.getElementById('tog-dayToggles');
    DOM.headerRow = document.getElementById('tog-headerRow');
    DOM.calendarGrid = document.getElementById('tog-calendarGrid');
    DOM.monthTotal = document.getElementById('tog-monthTotal');
    DOM.monthAvg = document.getElementById('tog-monthAvg');
    DOM.monthStatus = document.getElementById('tog-monthStatus');
    DOM.expectedDaily = document.getElementById('tog-expectedDaily');

    DOM.avatarBtn = document.getElementById('tog-user-avatar-btn');
    DOM.dropdown = document.getElementById('tog-user-dropdown');
    DOM.menuContainer = document.getElementById('tog-user-menu-container');
    DOM.menuAvatar = document.getElementById('tog-menu-avatar');
    DOM.userName = document.getElementById('tog-menu-user-name');
    DOM.userEmail = document.getElementById('tog-menu-user-email');

    DOM.backupBtn = document.getElementById('tog-backup-btn');
    DOM.restoreBtn = document.getElementById('tog-restore-btn');
    DOM.resetBtn = document.getElementById('tog-reset-btn');
    DOM.restoreInput = document.getElementById('tog-restore-input');
    DOM.trackerLogoBtn = document.getElementById('tog-tracker-logo-btn');
    DOM.leaveLegendContainer = document.getElementById('tog-leave-legend-container');

    DOM.convertersWrapper = document.getElementById('tog-converters-wrapper');
    DOM.visibleDaysWrapper = document.getElementById('tog-visible-days-wrapper');
    DOM.toggleConvertersBtn = document.getElementById('tog-toggle-converters-btn');
    DOM.toggleVisibleDaysBtn = document.getElementById('tog-toggle-visible-days-btn');
    DOM.toggleHalfDayCappingBtn = document.getElementById('tog-toggle-half-day-capping-btn');
    DOM.halfDayCappingIcon = document.getElementById('tog-half-day-capping-icon');

    DOM.converterBtn = document.getElementById('tog-data-converter-btn');
    DOM.converterModal = document.getElementById('tog-data-converter-modal');
    DOM.converterInput = document.getElementById('tog-converter-input');
    DOM.converterConvertBtn = document.getElementById('tog-converter-convert-btn');
    DOM.converterDownloadBtn = document.getElementById('tog-converter-download-btn');
    DOM.converterRestoreBtn = document.getElementById('tog-converter-restore-btn');
    DOM.converterOutput = document.getElementById('tog-converter-output');
    DOM.converterCancelBtn = document.getElementById('tog-converter-cancel-btn');

    DOM.helpBtn = document.getElementById('tog-help-btn');
    DOM.helpModal = document.getElementById('tog-help-modal');
    DOM.closeHelpBtn = document.getElementById('tog-close-help-btn');

    DOM.overlayInfoModal = document.getElementById('tog-overlay-info-modal');
    DOM.closeOverlayInfoBtn = document.getElementById('tog-overlay-info-close-btn');
}

let convertedJSON = null;

function openTogDataConverterModal() {
    if (DOM.converterModal) {
        DOM.converterModal.classList.add('visible');
    }
    if (DOM.converterInput) {
        DOM.converterInput.value = '';
    }
    if (DOM.converterOutput) {
        DOM.converterOutput.classList.add('hidden');
        DOM.converterOutput.textContent = '';
    }
    if (DOM.converterRestoreBtn) {
        DOM.converterRestoreBtn.disabled = true;
        DOM.converterRestoreBtn.classList.add('hidden');
    }
    if (DOM.converterDownloadBtn) {
        DOM.converterDownloadBtn.disabled = true;
        DOM.converterDownloadBtn.classList.add('hidden');
    }
    convertedJSON = null;
    closeDropdown();
}

function closeTogDataConverterModal() {
    if (DOM.converterModal) {
        DOM.converterModal.classList.remove('visible');
    }
}

function convertTogData() {
    if (!DOM.converterInput) return;

    const text = DOM.converterInput.value;
    const lines = text.split('\n').map(l => l.trim()).filter(l => l !== '');
    const result = {};

    let currentYear = '';
    let currentMonth = '';

    const monthMap = {
        "January": "01", "February": "02", "March": "03", "April": "04",
        "May": "05", "June": "06", "July": "07", "August": "08",
        "September": "09", "October": "10", "November": "11", "December": "12"
    };

    const monthYearRegex = /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        const monthMatch = line.match(monthYearRegex);
        if (monthMatch) {
            let m = monthMatch[1].charAt(0).toUpperCase() + monthMatch[1].slice(1).toLowerCase();
            currentMonth = monthMap[m];
            currentYear = monthMatch[2];
            continue;
        }

        if (/^\d{1,2}$/.test(line)) {
            const currentDay = line.padStart(2, '0');

            if (i + 1 < lines.length) {
                const nextLine = lines[i + 1];
                if (nextLine.endsWith('hr')) {
                    const val = nextLine.replace('hr', '');
                    if (currentYear && currentMonth) {
                        const dateStr = `${currentYear}-${currentMonth}-${currentDay}`;
                        result[dateStr] = val;
                    }
                }
            }
        }
    }

    if (Object.keys(result).length > 0) {
        convertedJSON = result;
        if (DOM.converterOutput) {
            DOM.converterOutput.textContent = JSON.stringify(result, null, 2);
            DOM.converterOutput.classList.remove('hidden');
        }
        if (DOM.converterRestoreBtn) {
            DOM.converterRestoreBtn.disabled = false;
            DOM.converterRestoreBtn.classList.remove('hidden');
        }
        if (DOM.converterDownloadBtn) {
            DOM.converterDownloadBtn.disabled = false;
            DOM.converterDownloadBtn.classList.remove('hidden');
        }
    } else {
        if (DOM.converterOutput) {
            DOM.converterOutput.textContent = "No valid 'hr' data found. Please check your input format.";
            DOM.converterOutput.classList.remove('hidden');
        }
        if (DOM.converterRestoreBtn) {
            DOM.converterRestoreBtn.disabled = true;
            DOM.converterRestoreBtn.classList.add('hidden');
        }
        if (DOM.converterDownloadBtn) {
            DOM.converterDownloadBtn.disabled = true;
            DOM.converterDownloadBtn.classList.add('hidden');
        }
        convertedJSON = null;
    }
}

function downloadConvertedTogData() {
    if (!convertedJSON) return;

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(convertedJSON, null, 2));
    const downloadAnchorNode = document.createElement('a');

    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "converted_data.json");
    document.body.appendChild(downloadAnchorNode); // Required for Firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
}

async function restoreConvertedTogData() {
    if (!convertedJSON) return;

    state.storedData = { ...state.storedData, ...convertedJSON };
    if(state.storedData._dayVisibility) state.dayVisibility = state.storedData._dayVisibility;

    localStorage.setItem(LOCAL_STORAGE_KEYS.TOG_DATA, JSON.stringify(state.storedData));

    if(state.userId) {
        const nestedData = convertFlatToNested(state.storedData);
        const userRef = doc(state.db, COLLECTIONS.USERS, state.userId);
        await updateDoc(userRef, { togData: nestedData });
    }

    renderCalendar();
    showToast(state.i18n?.t('tog.restored') || "Restored Successfully", "success");
    closeTogDataConverterModal();
}

function bindEvents() {
    if(DOM.expectedDaily) {
        DOM.expectedDaily.addEventListener('input', (e) => {
            const val = parseFloat(e.target.value) || 0;
            state.expectedDaily = val;
            saveData('_expectedDaily', val);
            renderCalendar();
        });
    }

    if(DOM.c1_h) [DOM.c1_h, DOM.c1_m].forEach(el => el.addEventListener('input', calcTime));
    if(DOM.c2_d) DOM.c2_d.addEventListener('input', calcDecimal);

    document.getElementById('tog-prev-month-btn')?.addEventListener('click', () => changeMonth(-1));
    document.getElementById('tog-next-month-btn')?.addEventListener('click', () => changeMonth(1));
    document.getElementById('tog-today-btn')?.addEventListener('click', goToToday);

    // Month Picker
    DOM.monthLabel.addEventListener('click', () => {
        if (window.openSharedMonthPicker) {
            window.openSharedMonthPicker(state.viewDate, (newDate) => {
                state.viewDate = newDate;
                renderCalendar();
            });
        }
    });

    // Avatar Menu
    if(DOM.avatarBtn) {
        DOM.avatarBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleDropdown();
        });
        document.addEventListener('click', (e) => {
            if(DOM.menuContainer && !DOM.menuContainer.contains(e.target)) {
                closeDropdown();
            }
        });
    }

    DOM.backupBtn?.addEventListener('click', backupData);
    DOM.restoreBtn?.addEventListener('click', () => DOM.restoreInput.click());
    DOM.restoreInput?.addEventListener('change', handleRestore);

    DOM.resetBtn?.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('tog-reset-request'));
        closeDropdown();
    });

    DOM.trackerLogoBtn?.addEventListener('click', toggleTrackerOverlay);

    DOM.toggleConvertersBtn?.addEventListener('click', toggleConverters);
    DOM.toggleVisibleDaysBtn?.addEventListener('click', toggleVisibleDays);
    DOM.toggleHalfDayCappingBtn?.addEventListener('click', toggleHalfDayCapping);

    DOM.converterBtn?.addEventListener('click', openTogDataConverterModal);
    DOM.converterConvertBtn?.addEventListener('click', convertTogData);
    DOM.converterDownloadBtn?.addEventListener('click', downloadConvertedTogData);
    DOM.converterRestoreBtn?.addEventListener('click', restoreConvertedTogData);
    DOM.converterCancelBtn?.addEventListener('click', closeTogDataConverterModal);

    // How to use TOG Tracker help modal
    DOM.helpBtn?.addEventListener('click', () => {
        DOM.helpModal?.classList.add('visible');
        closeDropdown();
    });
    DOM.closeHelpBtn?.addEventListener('click', () => {
        DOM.helpModal?.classList.remove('visible');
    });
    DOM.helpModal?.addEventListener('click', (e) => {
        if (e.target === DOM.helpModal) {
            DOM.helpModal.classList.remove('visible');
        }
    });

    // First-time overlay info modal listeners
    DOM.closeOverlayInfoBtn?.addEventListener('click', () => {
        DOM.overlayInfoModal?.classList.remove('visible');
    });
    DOM.overlayInfoModal?.addEventListener('click', (e) => {
        if (e.target === DOM.overlayInfoModal) {
            DOM.overlayInfoModal.classList.remove('visible');
        }
    });

    document.getElementById('tog-logout-btn')?.addEventListener('click', () => {
        if(window.appSignOut) window.appSignOut();
    });

    // Make window functions for inline HTML clicks (legacy support)
    window.tog_insertValue = insertValue;
    window.tog_handleInputChange = handleInputChange;
}

export function refreshTogUI() {
    cacheDOM();
    renderHeader(state.auth?.currentUser || null);
    renderCalendar(true);
}

export function updateLeaveData(yearlyData, leaveTypes) {
    state.trackerYearlyData = yearlyData || {};
    state.trackerLeaveTypes = leaveTypes || [];
    applyHalfDayCappingToAll();
    cacheDOM();
    if (state.showTrackerData && DOM.calendarGrid) {
        renderCalendar(true);
    }
}

function applyToggleState() {
    if (DOM.convertersWrapper) {
        if (state.showConverters) DOM.convertersWrapper.classList.add('visible');
        else DOM.convertersWrapper.classList.remove('visible');
    }
    if (DOM.visibleDaysWrapper) {
        if (state.showVisibleDays) DOM.visibleDaysWrapper.classList.add('visible');
        else DOM.visibleDaysWrapper.classList.remove('visible');
    }
    if (DOM.halfDayCappingIcon) {
        if (state.halfDayCapping) {
            DOM.halfDayCappingIcon.classList.remove('fa-toggle-off', 'text-gray-400');
            DOM.halfDayCappingIcon.classList.add('fa-toggle-on', 'text-blue-500');
        } else {
            DOM.halfDayCappingIcon.classList.remove('fa-toggle-on', 'text-blue-500');
            DOM.halfDayCappingIcon.classList.add('fa-toggle-off', 'text-gray-400');
        }
    }
    updateTrackerOverlayUI();
}

function toggleConverters() {
    state.showConverters = !state.showConverters;
    applyToggleState();
    saveData('_showConverters', state.showConverters);
    closeDropdown();
}

function toggleVisibleDays() {
    state.showVisibleDays = !state.showVisibleDays;
    applyToggleState();
    saveData('_showVisibleDays', state.showVisibleDays);
    closeDropdown();
}

export function convertFlatToNested(flatData) {
    const nested = {};
    if (!flatData || typeof flatData !== 'object') return nested;

    for (const key in flatData) {
        if (key.startsWith('_')) {
            nested[key] = flatData[key];
        } else {
            let datePart = key.startsWith('bonus_') ? key.replace('bonus_', '') : key;
            const parts = datePart.split('-');
            if (parts.length === 3) {
                const [year, month] = parts;
                if (!nested[year]) nested[year] = {};
                if (!nested[year][month]) nested[year][month] = {};
                if (!nested[year][month][datePart]) nested[year][month][datePart] = {};

                const val = flatData[key];
                if (val !== undefined && val !== "") {
                    if (key.startsWith('bonus_')) {
                        nested[year][month][datePart].bonus = val;
                    } else {
                        nested[year][month][datePart].main = val;
                    }
                }
            } else {
                nested[key] = flatData[key];
            }
        }
    }
    return nested;
}

function applyHalfDayCappingToAll() {
    // Dynamic capping is applied on-the-fly during render rendering,
    // so we no longer mutate and repeatedly double raw storedData entries in Firestore.
}

function toggleHalfDayCapping() {
    state.halfDayCapping = !state.halfDayCapping;
    applyToggleState();
    saveData('_halfDayCapping', state.halfDayCapping);
    renderCalendar();
    closeDropdown();
}

function updateTrackerOverlayUI() {
    if (DOM.trackerLogoBtn) {
        DOM.trackerLogoBtn.style.boxShadow = 'none';
        if (state.showTrackerData) {
            DOM.trackerLogoBtn.classList.add('active');
        } else {
            DOM.trackerLogoBtn.classList.remove('active');
        }
    }

    if (DOM.leaveLegendContainer) {
        if (state.showTrackerData) {
            DOM.leaveLegendContainer.classList.add('visible');
            renderLeaveLegend();
        } else {
            DOM.leaveLegendContainer.classList.remove('visible');
        }
    }
}

function checkAndShowTogOverlayModal() {
    const hasSeen = localStorage.getItem(LOCAL_STORAGE_KEYS.HAS_SEEN_TOG_OVERLAY_MODAL);
    if (!hasSeen) {
        // If main help modal is currently open, close it first or show overlay modal cleanly
        if (DOM.helpModal?.classList.contains('visible')) {
            DOM.helpModal.classList.remove('visible');
        }
        if (DOM.overlayInfoModal) {
            DOM.overlayInfoModal.classList.add('visible');
        }
        localStorage.setItem(LOCAL_STORAGE_KEYS.HAS_SEEN_TOG_OVERLAY_MODAL, 'true');
    }
}

function toggleTrackerOverlay() {
    state.showTrackerData = !state.showTrackerData;
    saveData('_showTrackerData', state.showTrackerData);
    updateTrackerOverlayUI();
    renderCalendar(true);
    checkAndShowTogOverlayModal();
}

function renderLeaveLegend() {
    if (!DOM.leaveLegendContainer) return;

    DOM.leaveLegendContainer.innerHTML = '';
    const currentYear = state.viewDate.getFullYear();
    const yearData = state.trackerYearlyData[currentYear] || {};
    const overrides = yearData.leaveOverrides || {};

    const visibleTypes = state.trackerLeaveTypes.filter(lt => {
        if (overrides[lt.id]?.hidden) return false;
        if (lt.limitYear && lt.limitYear !== currentYear) return false;
        return true;
    });

    visibleTypes.forEach(lt => {
        const pill = document.createElement('div');
        pill.className = 'flex-shrink-0 truncate max-w-40 px-3 py-1.5 rounded-full text-sm font-semibold text-white shadow';
        pill.style.backgroundColor = lt.color;
        pill.innerText = lt.name;
        DOM.leaveLegendContainer.appendChild(pill);
    });
}

function subscribeToData(userId) {
    if(state.unsubscribe) state.unsubscribe();
    if(!state.db) return;

    const userRef = doc(state.db, COLLECTIONS.USERS, userId);
    state.unsubscribe = onSnapshot(userRef, (docSnap) => {
        if(docSnap.exists()) {
            const data = docSnap.data();
            const cloudTogData = data.togData || {};

            // Flatten nested structure (year/month/data) and combine with root/flat keys
            let flatCloudData = {};

            // 1. Process nested year maps (e.g. cloudTogData["2026"]["06"]["2026-06-29"] = { main: "0.26", bonus: "0.67" })
            for (const key in cloudTogData) {
                if (/^\d{4}$/.test(key) && typeof cloudTogData[key] === 'object' && cloudTogData[key] !== null) {
                    const yearData = cloudTogData[key];
                    for (const monthKey in yearData) {
                        const monthData = yearData[monthKey];
                        if (typeof monthData === 'object' && monthData !== null) {
                            for (const dateKey in monthData) {
                                const dayValue = monthData[dateKey];
                                if (typeof dayValue === 'object' && dayValue !== null) {
                                    if (dayValue.main !== undefined) flatCloudData[dateKey] = dayValue.main;
                                    if (dayValue.bonus !== undefined) flatCloudData[`bonus_${dateKey}`] = dayValue.bonus;
                                } else {
                                    flatCloudData[dateKey] = dayValue;
                                }
                            }
                        }
                    }
                }
            }

            // 2. Process root/legacy keys (settings, flat date strings, or root date objects from restore)
            // Flat date keys at root level take precedence if present (e.g. freshly restored backup data)
            for (const key in cloudTogData) {
                if (key.startsWith('_')) {
                    flatCloudData[key] = cloudTogData[key];
                } else if (!/^\d{4}$/.test(key)) {
                    const val = cloudTogData[key];
                    if (typeof val === 'object' && val !== null) {
                        if (val.main !== undefined) flatCloudData[key] = val.main;
                        if (val.bonus !== undefined) flatCloudData[`bonus_${key}`] = val.bonus;
                    } else if (val !== undefined && val !== "") {
                        flatCloudData[key] = val;
                    }
                }
            }

            // Merge strategy: Cloud wins
            state.storedData = { ...state.storedData, ...flatCloudData };

            if(state.storedData._dayVisibility) {
                state.dayVisibility = state.storedData._dayVisibility;
            }
            if(state.storedData._showConverters !== undefined) state.showConverters = state.storedData._showConverters;
            if(state.storedData._showVisibleDays !== undefined) state.showVisibleDays = state.storedData._showVisibleDays;
            if(state.storedData._halfDayCapping !== undefined) state.halfDayCapping = state.storedData._halfDayCapping;
            if(state.storedData._showTrackerData !== undefined) state.showTrackerData = state.storedData._showTrackerData;
            if(state.storedData._expectedDaily !== undefined) {
                state.expectedDaily = state.storedData._expectedDaily;
                if (DOM.expectedDaily) DOM.expectedDaily.value = state.expectedDaily;
            }

            applyToggleState();

            // Update local storage for redundancy/offline fallback
            localStorage.setItem(LOCAL_STORAGE_KEYS.TOG_DATA, JSON.stringify(state.storedData));

            // If data doesn't have email, try to get it from Auth
            if (!data.email && state.auth && state.auth.currentUser) {
                data.email = state.auth.currentUser.email;
            }
            // If data doesn't have uid, add it
            if (!data.uid) {
                data.uid = userId;
            }

            renderHeader(data);

            // Render with focus preservation, deferred to match optimistic update and ensure focus stability
            setTimeout(() => renderCalendar(true), 0);
        } else {
            // Document doesn't exist yet, but user is logged in
            if (state.auth && state.auth.currentUser) {
                renderHeader({ email: state.auth.currentUser.email, uid: state.auth.currentUser.uid });
            }
            renderCalendar();
        }
    });
}

function renderHeader(user) {
    const effectiveUser = user || (state.auth && state.auth.currentUser);
    const displayUser = effectiveUser || null;
    const email = (effectiveUser && effectiveUser.email) ? effectiveUser.email : "";
    const displayName = effectiveUser ? effectiveUser.displayName : null;
    const effectiveName = displayName || (email ? email.split('@')[0] : (effectiveUser ? "User" : "Guest"));

    if (DOM.userName) {
        DOM.userName.innerText = effectiveName;
        DOM.userName.title = effectiveName;
    }

    if (DOM.userEmail) {
        DOM.userEmail.innerText = email;
        DOM.userEmail.title = email;
    }

    // Preserve default icon in top navbar button
    if (DOM.avatarBtn) {
        DOM.avatarBtn.innerHTML = `<svg class="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>`;
    }

    // Update Avatar inside Dropdown Menu with colorful initial gradient
    if (displayUser) {
        const seed = displayUser.uid || email || 'user';
        const initial = (displayName || email || 'U').charAt(0).toUpperCase();

        if (DOM.menuAvatar) {
            DOM.menuAvatar.innerHTML = generateAvatarSVG(seed, initial, 36);
        }
    } else {
        if (DOM.menuAvatar) {
            DOM.menuAvatar.innerHTML = generateAvatarSVG('guest', 'G', 36);
        }
        if (DOM.userName) {
            DOM.userName.innerText = state.i18n?.t ? (state.i18n.t('auth.guest') || 'Guest') : 'Guest';
        }
        if (DOM.userEmail) {
            DOM.userEmail.innerText = state.i18n?.t ? (state.i18n.t('auth.localGuestMode') || 'Local Guest Mode') : 'Local Guest Mode';
        }
    }
}

// --- Logic ---

function updateMemory(val) {
    state.lastCalculatedDecimal = val;
    if(DOM.globalMemory) DOM.globalMemory.innerText = val;
}

function calcTime() {
    const h = parseFloat(DOM.c1_h.value) || 0;
    const m = parseFloat(DOM.c1_m.value) || 0;
    const decimal = h + (m / 60);
    const fmt = Number.isInteger(decimal) ? decimal : decimal.toFixed(2);
    if(DOM.resDecimal) DOM.resDecimal.innerText = fmt;
    updateMemory(fmt);
}

function calcDecimal() {
    const val = parseFloat(DOM.c2_d.value) || 0;
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 60);
    if(DOM.resTime) DOM.resTime.innerText = (minutes === 60) ? `${hours + 1}h 0m` : `${hours}h ${minutes}m`;
    updateMemory(val);
}

function toggleDay(index) {
    state.dayVisibility[index] = !state.dayVisibility[index];
    saveData('_dayVisibility', state.dayVisibility);
    renderCalendar();
}

async function saveData(key, value) {
    if(value === "") { delete state.storedData[key]; }
    else { state.storedData[key] = value; }

    localStorage.setItem(LOCAL_STORAGE_KEYS.TOG_DATA, JSON.stringify(state.storedData));

    // Update UI immediately (optimistic update) - mainly for Guest mode or immediate feedback
    // Pass true to preserve focus so user can continue typing/tabbing
    // Defer render slightly to ensure focus has settled (e.g. if clicking from one input to another)
    setTimeout(() => renderCalendar(true), 0);

    if(state.userId && state.db) {
        const userRef = doc(state.db, COLLECTIONS.USERS, state.userId);

        if (key.startsWith('_')) {
             const fieldPath = `togData.${key}`;
             try {
                 if (value === "") await updateDoc(userRef, { [fieldPath]: deleteField() });
                 else await updateDoc(userRef, { [fieldPath]: value });
             } catch(e) {
                 const payload = { togData: { [key]: value === "" ? deleteField() : value } };
                 await setDoc(userRef, payload, { merge: true });
             }
        } else {
             // Parse date from key
             let datePart = key.startsWith('bonus_') ? key.replace('bonus_', '') : key;
             const [year, month] = datePart.split('-');

             if (!year || !month) {
                 // Fallback for weird keys
                 const fieldPath = `togData.${key}`;
                 await updateDoc(userRef, { [fieldPath]: value === "" ? deleteField() : value });
             } else {
                 // Construct the day object from state, but override with current input value
                 let mainVal = state.storedData[datePart];
                 let bonusVal = state.storedData[`bonus_${datePart}`];

                 // Override the specific field being updated right now
                 if (key === datePart) {
                     mainVal = value;
                 } else if (key === `bonus_${datePart}`) {
                     bonusVal = value;
                 }

                 const dayObj = {};
                 if (mainVal !== undefined && mainVal !== "") dayObj.main = mainVal;
                 if (bonusVal !== undefined && bonusVal !== "") dayObj.bonus = bonusVal;

                 const fieldPath = `togData.${year}.${month}.${datePart}`;

                 // Legacy/flat copies of this date may also exist at the root of togData
                 // (e.g. from a restore/import or an older format). We must always clear
                 // those flat keys too, otherwise the onSnapshot listener reads them back
                 // and "resurrects" a value the user just deleted. This is the cause of the
                 // intermittent "delete doesn't stick" bug.
                 const legacyMainPath = `togData.${datePart}`;
                 const legacyBonusPath = `togData.bonus_${datePart}`;

                 try {
                     if (Object.keys(dayObj).length === 0) {
                         // Deleting the whole day: remove nested AND any legacy flat copies.
                         await updateDoc(userRef, {
                             [fieldPath]: deleteField(),
                             [legacyMainPath]: deleteField(),
                             [legacyBonusPath]: deleteField()
                         });
                     } else {
                         // Writing the day: store nested, and remove legacy flat copies so
                         // there's a single source of truth (prevents stale flat values).
                         await updateDoc(userRef, {
                             [fieldPath]: dayObj,
                             [legacyMainPath]: deleteField(),
                             [legacyBonusPath]: deleteField()
                         });
                     }
                 } catch(e) {
                     console.warn("Update failed, trying set/merge", e);

                     // Reconstruct nested object for setDoc
                     const payload = { togData: {} };
                     if (!payload.togData[year]) payload.togData[year] = {};
                     if (!payload.togData[year][month]) payload.togData[year][month] = {};

                     if (Object.keys(dayObj).length === 0) {
                         payload.togData[year][month][datePart] = deleteField();
                     } else {
                         payload.togData[year][month][datePart] = dayObj;
                     }
                     // Also clear legacy flat copies in the fallback path.
                     payload.togData[datePart] = deleteField();
                     payload.togData[`bonus_${datePart}`] = deleteField();

                     await setDoc(userRef, payload, { merge: true });
                 }
             }
        }
    }
}

// --- Calendar ---
function getSunday(d) {
    d = new Date(d);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
}

function formatDateKey(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function renderCalendar(preserveFocus = false) {
    if(!DOM.calendarGrid) return;

    let focusedKey = null;
    let focusedType = null;
    if (preserveFocus && document.activeElement && document.activeElement.dataset.key) {
        focusedKey = document.activeElement.dataset.key;
        focusedType = document.activeElement.dataset.type;
    }

    if (state.showTrackerData) {
        renderLeaveLegend();
    }

    // 1. Toggles
    const daysKey = (state.i18n?.getValue('common.shortDays')) ? 'common.shortDays' : 'common.days';
    const dayNames = state.i18n?.getValue(daysKey) || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    DOM.dayToggles.innerHTML = '';
    dayNames.forEach((name, idx) => {
        const isVisible = state.dayVisibility[idx];
        const btn = document.createElement('button');
        const activeClass = isVisible
            ? "bg-blue-100 text-blue-700 border-blue-200 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
            : "bg-slate-50 text-slate-300 border-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-700";

        // Added flex-shrink-0 to prevent squeezing
        btn.className = `flex-shrink-0 flex items-center justify-center w-8 h-8 rounded text-[10px] font-bold border transition-colors ${activeClass}`;
        btn.onclick = () => toggleDay(idx);
        btn.innerText = name.charAt(0);
        DOM.dayToggles.appendChild(btn);
    });

    // 2. Grid Setup
    const visibleCount = state.dayVisibility.filter(Boolean).length;
    const gridCols = visibleCount + 1;
    const style = `grid-template-columns: repeat(${gridCols}, minmax(100px, 1fr)); min-width: 900px;`;
    DOM.headerRow.style.cssText = style;
    DOM.calendarGrid.style.cssText = style;

    // 3. Headers
    DOM.headerRow.innerHTML = '';
    dayNames.forEach((name, idx) => {
        if(!state.dayVisibility[idx]) return;
        const div = document.createElement('div');
        const textColor = idx === 0 ? "text-red-500" : "text-slate-400";
        div.className = `text-center text-xs font-bold ${textColor} uppercase py-1`;
        div.innerText = name;
        DOM.headerRow.appendChild(div);
    });
    const wkDiv = document.createElement('div');
    wkDiv.className = "text-center text-xs font-bold text-emerald-500 uppercase py-1 border-s border-slate-100 dark:border-slate-800";
    wkDiv.innerText = state.i18n?.t('tog.weekly') || "Weekly";
    DOM.headerRow.appendChild(wkDiv);

    // 4. Body
    DOM.calendarGrid.innerHTML = '';
    const year = state.viewDate.getFullYear();
    const month = state.viewDate.getMonth();
    const monthNames = state.i18n?.getValue('common.months') || ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    if(DOM.monthLabel) DOM.monthLabel.innerText = `${monthNames[month]} ${year}`;

    const translatedTotalLabel = (state.i18n?.t('tog.total') || 'Total').toLowerCase();
    const translatedWkAvgLabel = state.i18n?.t('tog.wkAvg') || "Wk Avg";

    const firstDayOfMonth = new Date(year, month, 1);
    const startDate = getSunday(firstDayOfMonth);

    // Force 6 weeks (42 days) to ensure consistent row count
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 41);

    const todayKey = formatDateKey(new Date());

    let currentLoopDate = new Date(startDate);
    let monthTotal = 0;
    let monthActiveDays = 0;

    while (currentLoopDate <= endDate) {
        let weekTotal = 0;
        let weekActiveDays = 0;
        const weekNodes = [];

        for (let i = 0; i < 7; i++) {
            const dateKey = formatDateKey(currentLoopDate);
            const bonusKey = `bonus_${dateKey}`;
            const rawVal = state.storedData[dateKey];
            const rawBonus = state.storedData[bonusKey];
            const val = rawVal !== undefined ? rawVal : '';
            const bonusVal = rawBonus !== undefined ? rawBonus : '';

            const isToday = dateKey === todayKey;
            const isCurrentMonth = currentLoopDate.getMonth() === month;
            const numVal = parseFloat(val) || 0;
            const numBonus = parseFloat(bonusVal) || 0;

            const currentYear = currentLoopDate.getFullYear();
            const trackerDayData = state.trackerYearlyData[currentYear]?.activities?.[dateKey];
            const isHalfDayLeave = trackerDayData?.leave?.dayType === 'half';

            // Apply half-day capping dynamically for display and totals without mutating raw storedData
            let dailySum = numVal + numBonus;
            let showTopTotalBadge = false;

            if (state.halfDayCapping && isHalfDayLeave) {
                if (numVal > 0 || numBonus > 0) {
                    // Cap half day leaves to HALF_DAY_CAPPING_MAX_HOURS (industry standard constant in constants.js)
                    dailySum = Math.min(HALF_DAY_CAPPING_MAX_HOURS, (numVal * 2) + numBonus);
                    showTopTotalBadge = true;
                }
            } else if (numBonus > 0) {
                showTopTotalBadge = true;
            }

            weekTotal += dailySum;

            if(isCurrentMonth) {
                monthTotal += dailySum;
                if ((rawVal !== undefined && rawVal !== "") || (rawBonus !== undefined && rawBonus !== "")) monthActiveDays++;
            }
            if ((rawVal !== undefined && rawVal !== "") || (rawBonus !== undefined && rawBonus !== "")) {
                weekActiveDays++;
            }

            if (state.dayVisibility[i]) {
                const baseOpacity = isCurrentMonth ? 'opacity-100' : 'opacity-40 hover:opacity-100 transition-opacity';
                let cardBg = isToday ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-slate-900';
                const cardBorder = isToday ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700';
                let dayText = isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-slate-500 dark:text-slate-400 font-medium';
                if (currentLoopDate.getDay() === 0) {
                    dayText = isToday ? 'text-red-500 font-bold' : 'text-red-500 font-medium';
                }
                const footerBg = 'bg-yellow-50 dark:bg-yellow-900/10';

                let overlayStyle = '';
                if (state.showTrackerData) {
                    const currentYear = currentLoopDate.getFullYear();
                    const trackerDayData = state.trackerYearlyData[currentYear]?.activities?.[dateKey];
                    if (trackerDayData?.leave) {
                        const leaveType = state.trackerLeaveTypes.find(lt => lt.id === trackerDayData.leave.typeId);
                        if (leaveType) {
                            // Convert hex to RGBA for consistency if possible, assuming hex color from picker
                            const hex = leaveType.color.startsWith('#') ? leaveType.color : '#3b82f6';
                            const alphaHex = '40'; // 25% opacity

                            if (trackerDayData.leave.dayType === 'half') {
                                // Half day: Linear gradient
                                // "half filled cells of the same leave type colour"
                                // We use the color with 25% opacity (alphaHex) for the filled part
                                const colorWithOpacity = `${hex}${alphaHex}`;
                                overlayStyle = `background-image: linear-gradient(to bottom right, ${colorWithOpacity} 50%, transparent 50%) !important;`;
                            } else {
                                // Full day
                                overlayStyle = `background-color: ${hex}${alphaHex} !important;`;
                            }
                        }
                    }
                }

                const card = document.createElement('div');
                card.className = `day-card min-h-[100px] border ${cardBorder} ${!overlayStyle ? cardBg : ''} ${baseOpacity}`;
                if (overlayStyle) {
                     card.style.cssText = overlayStyle;
                }

                const cellDateLabel = currentLoopDate.toLocaleDateString(state.i18n?.currentLang || undefined, { weekday: 'short', month: 'short', day: 'numeric' });

                // Render card layout
                card.innerHTML = `
                    <div class="day-header flex items-center justify-between">
                        <span class="${dayText}">${currentLoopDate.getDate()}</span>
                        ${showTopTotalBadge ? `<span role="status" aria-label="Total: ${dailySum.toFixed(2)} ${translatedTotalLabel}" class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#0071e3] text-white shadow-xs whitespace-nowrap font-mono" title="Total used: ${dailySum.toFixed(2)}">${dailySum.toFixed(2)} ${translatedTotalLabel}</span>` : ''}
                    </div>
                    <div class="day-body relative flex items-center">
                        <input type="number" step="0.1"
                            data-key="${dateKey}" data-type="main"
                            aria-label="${cellDateLabel} hours"
                            class="input-field bg-transparent border-none font-mono font-bold text-left text-[23px] leading-tight w-full text-slate-900 dark:text-white pr-8"
                            value="${val}" onchange="window.tog_handleInputChange('${dateKey}', this.value)">
                        <button onclick="window.tog_insertValue('${dateKey}', 'main')" class="icon-btn w-5 h-5 flex items-center justify-center text-slate-400 dark:text-slate-500 opacity-40 hover:opacity-100 active:opacity-100 focus:opacity-100 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 absolute right-[10px] top-1/2 -translate-y-1/2 transition-all" title="Insert Memory" aria-label="Insert memory hours for ${cellDateLabel}">
                            <!-- lucide arrow-down-to-line replacement -->
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line w-3 h-3"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
                        </button>
                    </div>
                    <div class="day-footer relative flex items-center">
                        <input type="number" step="0.1"
                            data-key="${dateKey}" data-type="bonus"
                            aria-label="${cellDateLabel} bonus hours"
                            class="input-field bg-transparent border-none font-mono font-bold text-left text-xs w-full text-yellow-700 dark:text-yellow-500 placeholder-yellow-300 dark:placeholder-yellow-700 tog-bonus-input pr-6"
                            value="${bonusVal}" placeholder="-" onchange="window.tog_handleInputChange('${bonusKey}', this.value)">
                        <button onclick="window.tog_insertValue('${bonusKey}', 'bonus')" class="icon-btn w-5 h-5 flex items-center justify-center text-yellow-600 dark:text-yellow-500 opacity-40 hover:opacity-100 active:opacity-100 focus:opacity-100 hover:text-yellow-700 dark:hover:text-yellow-400 hover:bg-yellow-100/80 dark:hover:bg-yellow-900/50 absolute right-1 top-1/2 -translate-y-1/2 transition-all" title="Insert Memory" aria-label="Insert bonus hours for ${cellDateLabel}">
                            <!-- lucide arrow-down-to-line replacement -->
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line w-3 h-3"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
                        </button>
                    </div>
                `;
                weekNodes.push(card);
            }
            currentLoopDate.setDate(currentLoopDate.getDate() + 1);
        }

        weekNodes.forEach(node => DOM.calendarGrid.appendChild(node));

        // Weekly Stats
        const weekExpected = weekActiveDays * state.expectedDaily;
        const weekDiff = weekTotal - weekExpected;
        const weekAvg = weekActiveDays > 0 ? (weekTotal / weekActiveDays) : 0;

        let weekStatusColorClass = "text-slate-700 dark:text-slate-300";
        let weekDiffString = "";

        if (weekDiff > 0) {
            weekStatusColorClass = "text-emerald-600 dark:text-emerald-400";
            weekDiffString = ` (+${weekDiff.toFixed(2)})`;
        } else if (weekDiff < 0) {
            weekStatusColorClass = "text-red-500 dark:text-red-400";
            weekDiffString = ` (${weekDiff.toFixed(2)})`;
        }

        const statCard = document.createElement('div');
        statCard.setAttribute('role', 'region');
        statCard.setAttribute('aria-label', `Weekly summary: average ${weekAvg.toFixed(2)}, total ${weekTotal.toFixed(2)}`);
        statCard.className = "stats-card min-h-[100px] bg-slate-100/70 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden flex flex-col justify-between";
        statCard.innerHTML = `
            <div class="day-header flex items-center justify-between px-2 pt-1.5 min-h-[22px]">
                <span class="text-[9px] uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500">${state.i18n?.t('tog.wkAvg') || "Wk Avg"}</span>
            </div>
            <div class="day-body relative flex flex-wrap items-center justify-between gap-x-1 gap-y-1 px-2 py-0.5">
                <div class="font-mono font-bold text-left text-[22px] leading-none ${weekStatusColorClass}">
                    ${weekAvg.toFixed(2)}
                </div>
                ${weekDiffString ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold ${weekDiff >= 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-700/50' : 'bg-red-100 text-red-800 dark:bg-red-950/70 dark:text-red-300 border border-red-200/80 dark:border-red-700/50'} shadow-xs font-mono whitespace-nowrap" title="Week ${weekDiff >= 0 ? 'Surplus' : 'Deficit'}">${weekDiffString.trim()}</span>` : ''}
            </div>
            <div class="day-footer relative flex items-center justify-between">
                <span class="text-[9px] uppercase font-bold text-blue-700 dark:text-blue-300 tracking-wider">${state.i18n?.t('tog.total') || "Total"}</span>
                <span class="text-xs font-mono font-bold text-blue-900 dark:text-blue-100">${weekTotal.toFixed(2)}</span>
            </div>
        `;
        DOM.calendarGrid.appendChild(statCard);
    }

    // Month Stats
    const monthExpected = monthActiveDays * state.expectedDaily;
    const monthDiff = monthTotal - monthExpected;
    const monthAvg = monthActiveDays > 0 ? (monthTotal / monthActiveDays) : 0;

    if(DOM.monthTotal) {
        DOM.monthTotal.innerText = monthTotal.toFixed(2);
        DOM.monthTotal.className = "text-base font-mono font-bold text-slate-900 dark:text-white leading-none";
    }

    if(DOM.monthAvg) {
        let monthStatusColorClass = "text-slate-900 dark:text-white";
        let monthDiffString = "";

        if (monthDiff > 0) {
            monthStatusColorClass = "text-emerald-600 dark:text-emerald-400";
            monthDiffString = `(+${monthDiff.toFixed(2)})`;
        } else if (monthDiff < 0) {
            monthStatusColorClass = "text-red-500 dark:text-red-400";
            monthDiffString = `(${monthDiff.toFixed(2)})`;
        }

        DOM.monthAvg.innerHTML = `
            <div class="flex items-baseline gap-1 ${monthStatusColorClass}">
                <span class="text-base font-mono font-bold leading-none">${monthAvg.toFixed(2)}</span>
                ${monthDiffString ? `<span class="text-[11px] font-mono font-bold opacity-80">${monthDiffString}</span>` : ''}
            </div>
        `;
        DOM.monthAvg.className = "w-full";
    }

    if (focusedKey) {
        const el = document.querySelector(`input[data-key="${focusedKey}"][data-type="${focusedType}"]`);
        if (el) el.focus();
    }
}

// --- Helpers ---

function handleInputChange(key, value) {
    saveData(key, value);
}

function insertValue(key, type) {
    if(state.lastCalculatedDecimal === 0) { showToast(state.i18n?.t('tog.calcFirst') || "Calculate first!", 'error'); return; }
    let valueToSave = state.lastCalculatedDecimal;
    saveData(key, valueToSave);
    showToast(state.i18n?.t('tog.inserted') || "Inserted!", 'success');
}

function changeMonth(offset) {
    state.viewDate.setMonth(state.viewDate.getMonth() + offset);
    renderCalendar();
}

function goToToday() { state.viewDate = new Date(); renderCalendar(); }

function showToast(msg, type = 'info') {
    if (window.showAppMessage) {
        window.showAppMessage(msg, type);
    } else {
        // Fallback if not available
        console.log("Toast:", msg);
    }
}

// --- Avatar Menu Logic ---

function toggleDropdown() {
    if(!DOM.dropdown) return;
    const isClosed = DOM.dropdown.classList.contains('opacity-0');
    if(isClosed) {
        DOM.dropdown.classList.remove('opacity-0', 'scale-95', 'pointer-events-none');
    } else {
        closeDropdown();
    }
}

function closeDropdown() {
    if(DOM.dropdown) DOM.dropdown.classList.add('opacity-0', 'scale-95', 'pointer-events-none');
}

// Backup (JSON)
function backupData() {
    const dataStr = JSON.stringify(state.storedData, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${TOG_BACKUP_PREFIX}${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    closeDropdown();
}

// Restore (JSON)
function handleRestore(e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
        try {
            const data = JSON.parse(event.target.result);
            state.storedData = { ...state.storedData, ...data };
            if(state.storedData._dayVisibility) state.dayVisibility = state.storedData._dayVisibility;

            localStorage.setItem(LOCAL_STORAGE_KEYS.TOG_DATA, JSON.stringify(state.storedData));

            if(state.userId) {
                // Convert flat restored data into canonical nested Firestore structure
                const nestedData = convertFlatToNested(state.storedData);
                const userRef = doc(state.db, COLLECTIONS.USERS, state.userId);
                await updateDoc(userRef, { togData: nestedData });
            }

            renderCalendar();
            showToast(state.i18n?.t('tog.restored') || "Restored Successfully", "success");
        } catch(err) {
            console.error(err);
            showToast(state.i18n?.t('tog.invalidJson') || "Invalid JSON File", "error");
        }
    };
    reader.readAsText(file);
    closeDropdown();
    e.target.value = ""; // Reset input
}

export async function performReset(userId, db) {
    state.storedData = { _dayVisibility: [true,true,true,true,true,true,true] };
    state.dayVisibility = [true,true,true,true,true,true,true];
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TOG_DATA);

    // Use passed context or fallback to state
    const targetUserId = userId || state.userId;
    const targetDb = db || state.db;

    if(targetUserId && targetDb) {
         const userRef = doc(targetDb, COLLECTIONS.USERS, targetUserId);
         await updateDoc(userRef, { togData: deleteField() });
    }
    renderCalendar();
    if (state.i18n) {
        showToast(state.i18n.t('tog.resetComplete'), "success");
    } else {
        showToast("Reset Complete", "success");
    }
}
